import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useUser, useClerk, SignIn } from '@clerk/clerk-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Checkbox } from '../components/ui/checkbox'
import { AlertTriangle, Trash2 } from 'lucide-react'
import { trpc } from '../trpc'
import { toast } from 'sonner'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import { Textarea } from '../components/ui/textarea'

export const Route = createFileRoute('/delete-account')({
  component: DeleteAccount,
})

// Deletion reason options
const DELETION_REASONS = [
  { value: 'too-expensive', label: 'Too expensive' },
  { value: 'not-using-enough', label: 'Not using it enough' },
  { value: 'missing-features', label: 'Missing features (please explain below)' },
  { value: 'found-alternative', label: 'Found an alternative (please explain below)' },
  { value: 'technical-issues', label: 'Technical issues (please explain below)' },
  { value: 'privacy-concerns', label: 'Privacy concerns (please explain below)' },
  { value: 'too-hard', label: "Don't understand/too hard to figure out (please explain below)" },
  { value: 'other', label: 'Other (please specify below)' },
] as const

// Reasons that require explanation text
const REASONS_REQUIRING_EXPLANATION = [
  'missing-features',
  'found-alternative',
  'technical-issues',
  'privacy-concerns',
  'too-hard',
  'other',
]

function DeleteAccount() {
  const { user, isLoaded } = useUser()
  const { signOut } = useClerk()
  const [confirmationEmail, setConfirmationEmail] = useState('')
  const [understandPermanence, setUnderstandPermanence] = useState(false)
  const [deletionReason, setDeletionReason] = useState('')
  const [deletionReasonText, setDeletionReasonText] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const deleteAccountMutation = trpc.users.deleteMyAccount.useMutation({
    onSuccess: async () => {
      // Account deleted successfully
      setIsLoading(false)
      
      // Show brief success message
      toast.success('Account deleted successfully', {
        description: 'Logging you out...',
        duration: 3000
      })
      
      // Log out the user and redirect to confirmation page
      try {
        await signOut()
        // Redirect to public confirmation page
        window.location.href = '/account-deleted'
      } catch (error) {
        console.error('Error during logout:', error)
        // Force redirect even if logout fails
        window.location.href = '/account-deleted'
      }
    },
    onError: (error: { message: string }) => {
      toast.error('Failed to delete account', {
        description: error.message
      })
      setIsLoading(false)
    }
  })

  // Show loading while Clerk is initializing
  if (!isLoaded) {
    return (
      <div className="container mx-auto max-w-2xl px-4 py-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      </div>
    )
  }

  // Show sign-in form if user is not authenticated
  if (!user) {
    return (
      <div className="container mx-auto max-w-2xl px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl text-red-600">Delete Your Account</CardTitle>
            <CardDescription>
              You must be signed in to delete your account
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-blue-800">
                Please sign in to your account to proceed with account deletion.
              </p>
            </div>
            
            <div className="flex justify-center">
              <SignIn 
                routing="hash"
                signUpUrl="#"
                redirectUrl="/delete-account"
              />
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Main account deletion form for authenticated users
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!user.primaryEmailAddress?.emailAddress) {
      toast.error('Unable to verify account email')
      return
    }

    if (confirmationEmail !== user.primaryEmailAddress.emailAddress) {
      toast.error('Email confirmation does not match your account email')
      return
    }

    if (!deletionReason) {
      toast.error('Please select a reason for deleting your account')
      return
    }

    if (REASONS_REQUIRING_EXPLANATION.includes(deletionReason) && !deletionReasonText.trim()) {
      toast.error('Please provide details about why you are deleting your account')
      return
    }

    if (!understandPermanence) {
      toast.error('You must confirm that you understand this action is permanent')
      return
    }

    setIsLoading(true)
    deleteAccountMutation.mutate({
      confirmationEmail: confirmationEmail,
      deletionReason: deletionReason,
      deletionReasonText: deletionReasonText.trim() || undefined,
    })
  }

  const isFormValid =
    confirmationEmail === user.primaryEmailAddress?.emailAddress &&
    deletionReason &&
    (!REASONS_REQUIRING_EXPLANATION.includes(deletionReason) || deletionReasonText.trim()) &&
    understandPermanence


  return (
    <div className="container mx-auto max-w-2xl px-4 py-8">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-2xl text-red-600">
            <Trash2 className="h-6 w-6" />
            Delete Your Account
          </CardTitle>
          <CardDescription>
            Permanently delete your Trackplicant account and all associated data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Critical Warning */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-red-800 mb-2">⚠️ This Action Cannot Be Undone</h3>
                <p className="text-red-700 mb-3">
                  Account deletion is permanent and irreversible. All your data will be completely removed from our systems.
                </p>
                <p className="text-red-700 text-sm">
                  If you have questions about this process, please contact our support team at{' '}
                  <a href="mailto:support@trackplicant.com" className="text-red-600 hover:underline font-medium">
                    support@trackplicant.com
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Account Information */}
          <div className="bg-gray-50 p-4 rounded-md">
            <h4 className="font-semibold mb-2">Account to be deleted:</h4>
            <div className="space-y-1 text-sm">
              <div>
                <span className="font-medium">Name:</span>{' '}
                {user.firstName || user.lastName
                  ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                  : 'No name provided'}
              </div>
              <div>
                <span className="font-medium">Email:</span> {user.primaryEmailAddress?.emailAddress}
              </div>
            </div>
          </div>

          {/* What Will Be Deleted */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="font-semibold text-red-800 mb-3">What Will Be Permanently Deleted</h3>
            <ul className="text-red-700 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-red-600 mt-1">•</span>
                <span>Your account profile and login credentials</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-600 mt-1">•</span>
                <span>All job applications and tracking data</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-600 mt-1">•</span>
                <span>Contact information and notes</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-600 mt-1">•</span>
                <span>Resume evaluations and scoring history</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-600 mt-1">•</span>
                <span>Analytics and usage data</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-600 mt-1">•</span>
                <span>Any uploaded files or documents</span>
              </li>
            </ul>
            <p className="mt-3 text-sm font-semibold text-red-800">
              Billing history will be preserved for legal compliance.
            </p>
          </div>

          {/* Confirmation Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="deletionReason">
                Why are you deleting your account? *
              </Label>
              <Select
                value={deletionReason}
                onValueChange={setDeletionReason}
                disabled={isLoading}
              >
                <SelectTrigger id="deletionReason" className="mt-1">
                  <SelectValue placeholder="Select a reason..." />
                </SelectTrigger>
                <SelectContent>
                  {DELETION_REASONS.map((reason) => (
                    <SelectItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="deletionReasonText">
                Additional details{' '}
                {deletionReason && REASONS_REQUIRING_EXPLANATION.includes(deletionReason) && (
                  <span className="text-red-600">*</span>
                )}
              </Label>
              <Textarea
                id="deletionReasonText"
                value={deletionReasonText}
                onChange={(e) => setDeletionReasonText(e.target.value)}
                placeholder="Tell us more about why you're deleting your account..."
                disabled={isLoading}
                className="mt-1"
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="confirmationEmail">
                Type your email address to confirm deletion:
              </Label>
              <Input
                id="confirmationEmail"
                type="email"
                value={confirmationEmail}
                onChange={(e) => setConfirmationEmail(e.target.value)}
                placeholder={user.primaryEmailAddress?.emailAddress}
                disabled={isLoading}
                className="mt-1"
                autoComplete="off"
              />
              <p className="text-xs text-gray-500 mt-1">
                Expected: {user.primaryEmailAddress?.emailAddress}
              </p>
            </div>

            <div className="flex items-start space-x-3">
              <Checkbox
                id="understand-permanence"
                checked={understandPermanence}
                onCheckedChange={(checked) => setUnderstandPermanence(checked === true)}
                disabled={isLoading}
                className="mt-1"
              />
              <Label 
                htmlFor="understand-permanence" 
                className="text-sm leading-5 cursor-pointer"
              >
                I understand that this action is permanent and cannot be undone. 
                All my data will be permanently deleted from Trackplicant.
              </Label>
            </div>

            <div className="flex flex-col gap-3 pt-4">
              <Button
                type="submit"
                variant="destructive"
                disabled={!isFormValid || isLoading}
                className="w-full"
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Deleting Account...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Trash2 className="h-4 w-4" />
                    Delete My Account Permanently
                  </div>
                )}
              </Button>
              
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (window.history.length > 1) {
                    window.history.back()
                  } else {
                    window.location.href = '/dashboard'
                  }
                }}
                disabled={isLoading}
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </form>

          {/* Support Contact */}
          <div className="border-t pt-4">
            <p className="text-center text-gray-600 text-sm">
              Having second thoughts or need help?{' '}
              <a href="mailto:support@trackplicant.com" className="text-blue-600 hover:underline">
                Contact our support team
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}